import { type JournalNote, createJournalNote } from '../../journal';
import { type Post, createPostInContentObject } from '../../post';
import type {
  CommunityPostContentObjectDetailDataObject,
  CommunityPostContentObjectListDataObject,
  GroupPostContentObjectDetailDataObject,
  GroupPostContentObjectListDataObject,
  PostContentObjectDetailDataObject,
  PostContentObjectListDataObject,
  ProgramPostContentObjectDetailDataObject,
  ProgramPostContentObjectListDataObject,
  PublicPostContentObjectDataObject,
} from '../../service';
import { createUploadedFile } from '../../uploaded-file';
import {
  type CommunityContentObjectDetail,
  type CommunityContentObjectList,
  type GroupContentObjectDetail,
  type GroupContentObjectList,
  type ProgramContentObject,
  type PublicCommunityContentObject,
  createCommunityContentObjectDetail,
  createCommunityContentObjectList,
  createGroupContentObjectDetail,
  createGroupContentObjectList,
  createProgramContentObject,
  createPublicCommunityContentObject,
} from '../contentable-type-content-objects';

export type CommunityPostContentObjectDetail = {
  post: Post;
  sharedContentObjectId?: string;
  type: 'post';
} & CommunityContentObjectDetail;

export type CommunityPostContentObjectList = {
  post: Post;
  sharedContentObjectId?: string;
  type: 'post';
} & CommunityContentObjectList;

export type GroupPostContentObjectDetail = {
  post: Post;
  sharedContentObjectId?: string;
  type: 'post';
} & GroupContentObjectDetail;

export type GroupPostContentObjectList = {
  post: Post;
  sharedContentObjectId?: string;
  type: 'post';
} & GroupContentObjectList;

export type PostContentObjectDetail =
  | CommunityPostContentObjectDetail
  | GroupPostContentObjectDetail
  | JournalNote
  | ProgramPostContentObjectDetail;

export type PostContentObjectList =
  | CommunityPostContentObjectList
  | GroupPostContentObjectList
  | JournalNote
  | ProgramPostContentObjectList;

export type ProgramPostContentObjectDetail = {
  post: Post;
  sharedContentObjectId?: string;
  type: 'post';
} & ProgramContentObject;

export type ProgramPostContentObjectList = {
  post: Post;
  sharedContentObjectId?: string;
  type: 'post';
} & ProgramContentObject;

export const createGroupPostContentObjectDetail = (
  data: GroupPostContentObjectDetailDataObject,
): GroupPostContentObjectDetail => ({
  ...createGroupContentObjectDetail(data),
  attachments: data.attachments.map((file) => createUploadedFile(file)),
  post: createPostInContentObject(data.post),
  sharedContentObjectId: data.shared_content_object?.id,
  type: 'post',
});

export const createCommunityPostContentObjectDetail = (
  data: CommunityPostContentObjectDetailDataObject,
): CommunityPostContentObjectDetail => ({
  ...createCommunityContentObjectDetail(data),
  attachments: data.attachments.map((file) => createUploadedFile(file)),
  post: createPostInContentObject(data.post),
  sharedContentObjectId: data.shared_content_object?.id,
  type: 'post',
});

export const createProgramPostContentObjectDetail = (
  data: ProgramPostContentObjectDetailDataObject,
): ProgramPostContentObjectDetail => ({
  ...createProgramContentObject(data),
  attachments: data.attachments.map((file) => createUploadedFile(file)),
  post: createPostInContentObject(data.post),
  sharedContentObjectId: data.shared_content_object?.id,
  type: 'post',
});

export const createPostContentObjectDetail = (data: PostContentObjectDetailDataObject): PostContentObjectDetail => {
  if (!('contentable_type' in data)) {
    return createJournalNote(data);
  }

  switch (data.contentable_type) {
    case 'Community':
      return createCommunityPostContentObjectDetail(data);
    case 'Group':
      return createGroupPostContentObjectDetail(data);
    case 'Program':
      return createProgramPostContentObjectDetail(data);
    default:
      throw new Error('Unknown contentable type');
  }
};

export const createGroupPostContentObjectList = (
  data: GroupPostContentObjectListDataObject,
): GroupPostContentObjectList => ({
  ...createGroupContentObjectList(data),
  attachments: data.attachments.map((file) => createUploadedFile(file)),
  post: createPostInContentObject(data.post),
  sharedContentObjectId: data.shared_content_object?.id,
  type: 'post',
});

export const createCommunityPostContentObjectList = (
  data: CommunityPostContentObjectListDataObject,
): CommunityPostContentObjectList => ({
  ...createCommunityContentObjectList(data),
  attachments: data.attachments.map((file) => createUploadedFile(file)),
  post: createPostInContentObject(data.post),
  sharedContentObjectId: data.shared_content_object?.id,
  type: 'post',
});

export const createProgramPostContentObjectList = (
  data: ProgramPostContentObjectListDataObject,
  // eslint-disable-next-line sonarjs/no-identical-functions -- Its the same but only for now
): ProgramPostContentObjectList => ({
  ...createProgramContentObject(data),
  attachments: data.attachments.map((file) => createUploadedFile(file)),
  post: createPostInContentObject(data.post),
  sharedContentObjectId: data.shared_content_object?.id,
  type: 'post',
});

export const createPostContentObjectList = (data: PostContentObjectListDataObject): PostContentObjectList => {
  if (!('contentable_type' in data)) {
    return createJournalNote(data);
  }

  switch (data.contentable_type) {
    case 'Community':
      return createCommunityPostContentObjectList(data);
    case 'Group':
      return createGroupPostContentObjectList(data);
    case 'Program':
      return createProgramPostContentObjectList(data);
    default:
      throw new Error('Unknown contentable type');
  }
};

export const isGroupPostContentObjectList = (
  contentObject: PostContentObjectList,
): contentObject is GroupPostContentObjectList => contentObject.contentableType === 'Group';

export type PublicCommunityPostContentObject = {
  post: Post;
  sharedContentObjectId?: string;
  type: 'post';
} & PublicCommunityContentObject;

export type PublicPostContentObject = PublicCommunityPostContentObject;

export const createPublicCommunityPostContentObject = (
  data: PublicPostContentObjectDataObject,
): PublicCommunityPostContentObject => ({
  ...createPublicCommunityContentObject(data),
  post: createPostInContentObject(data.post),
  sharedContentObjectId: data.shared_content_object?.id,
  type: 'post',
});

export const createPublicPostContentObject = (data: PublicPostContentObjectDataObject): PublicPostContentObject =>
  createPublicCommunityPostContentObject(data);
